import { Helper } from '@/mixin/helper.js'
import WsVoip from './ws.js'
import { float32ToInt16PCM } from './utils.js'
import { alawFromPCM } from './g711a.js'
import { playG711Audio } from './play-audio.js'

export default class Voip {
  constructor(token, vehicleId, vueInstance) {
    this.audioContext = null
    this.sourceNode = null
    this.scriptProcessorNode = null
    this.mediaStream = null
    this.socket = null
    this.token = token
    this.vueInstance = vueInstance
    this.recording = []
    this.recorder = null
    this.wavWorker = null
    this.g711Processor = null
    this.bufferSize = Number(vueInstance.getFeatureFlagValue('trackvision_voip_buffer_size') || 640)
    this.micThreshold = {
      rms: Number(vueInstance.getFeatureFlagValue('trackvision_voip_threshold_rms') || 0.05),
      stdDev: Number(vueInstance.getFeatureFlagValue('trackvision_voip_threshold_stddev') || 0.03),
      amplitude: Number(
        vueInstance.getFeatureFlagValue('trackvision_voip_threshold_amplitude') || 0.2
      ),
    }
    this.isDeviceMuted = false
    this.lastMessageTimestamp = Date.now()
    this.messageTimeout = 3000
    this.checkMessageInterval = null
    this.isMessageHaveBeenReceived = false
    this.isDeviceBuffering = false
    this.filter = null
    this.gain = null

    // setup noise cancellation
    const hasScope = vueInstance.hasAccessAll(['b3o-trackvision-noise-cancellation:read'])
    const isVehicleFilterNoiseCancellationActive = vueInstance.getFeatureFlagValue(
      'is_vehicle_filter_noise_cancellation'
    )
    const listVehicleIdNoiseCancellation =
      vueInstance.getFeatureFlagValue('list_vehicle_noise_cancellation') ?? []
    let calculatedHasNoiseCancellation = false
    if (
      (hasScope &&
        isVehicleFilterNoiseCancellationActive &&
        listVehicleIdNoiseCancellation.includes(vehicleId)) ||
      (hasScope && !isVehicleFilterNoiseCancellationActive)
    ) {
      calculatedHasNoiseCancellation = true
    }
    this.calculatedHasNoiseCancellation = calculatedHasNoiseCancellation

    // ws url
    this.wsDomain = calculatedHasNoiseCancellation
      ? process.env.VUE_APP_VOIP_WS_URL_SERVER_INDO
      : process.env.VUE_APP_VOIP_WS_URL
  }
  // Function to start capturing audio
  async startCapture(
    callbackCallActiveAfterBuffering,
    callbackDeviceBuffering,
    callbackCallsOnGoing,
    callbackCallsFailed,
    callbackWsError
  ) {
    // eslint-disable-next-line no-useless-catch
    try {
      this.mediaStream = await navigator.mediaDevices.getUserMedia({
        audio: {
          noiseSuppression: true,
        },
      })

      this.audioContext = new window.AudioContext({
        sampleRate: 8000,
      })

      this.sourceNode = this.audioContext.createMediaStreamSource(this.mediaStream)
      this.filter = this.audioContext.createBiquadFilter()
      this.gain = this.audioContext.createGain()

      this.socket = new WsVoip()

      await this.socket.connect(
        `${this.wsDomain}/ws?token=` + this.token,
        this.addEventListenerWebSocket(
          callbackCallActiveAfterBuffering,
          callbackDeviceBuffering,
          callbackCallsOnGoing,
          callbackCallsFailed,
          callbackWsError
        )
      )

      // Load both worklets
      await this.audioContext.audioWorklet.addModule('/voip/recorder.worklet.js')
      await this.audioContext.audioWorklet.addModule('/voip/g711-processor.js')

      this.recorder = new AudioWorkletNode(this.audioContext, 'recorder.worklet')
      this.g711Processor = new AudioWorkletNode(this.audioContext, 'g711-processor')

      this.filter.Q.value = 0.8
      this.filter.gain.value = 2
      this.filter.frequency.value = 2400
      this.filter.type = 'lowshelf'
      this.filter.connect(this.recorder)

      this.gain.gain.value = 3
      this.gain.connect(this.filter)

      this.sourceNode.connect(this.gain)

      // Connect the audio chain:
      // sourceNode -> recorder -> g711Processor -> destination
      this.sourceNode.connect(this.recorder)
      this.recorder.connect(this.g711Processor)
      this.g711Processor.connect(this.audioContext.destination)
      this.recorder.connect(this.audioContext.destination)

      this.recorder.port.postMessage({
        bufferSize: this.bufferSize,
        threshold: this.micThreshold,
        hasNoiseCancellation: this.calculatedHasNoiseCancellation,
      })

      this.recorder.port.onmessage = (e) => {
        let x = float32ToInt16PCM(e.data)
        const newdata = alawFromPCM(x)
        this.socket.send(newdata)
      }

      await Helper.methods.delay(2000)
      return true
    } catch (err) {
      this.vueInstance.$mcToast.error(this.vueInstance.$t('global.somethingWentWrong'))
      console.log(err)
      callbackCallsFailed()
    }
  }

  // Function to stop capturing audio
  stopCapture() {
    this.recording = []
    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => {
        if (track.readyState === 'live') {
          track.stop()
        }
      })
    }
    if (this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close()
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect()
    }
    if (this.scriptProcessorNode) {
      this.scriptProcessorNode.disconnect()
    }
    if (this.socket) {
      this.socket.close()
    }
    if (this.recorder) {
      this.recorder.disconnect()
    }
  }
  // Then add a method to control mute/unmute
  toggleMuteDevice(isMuted) {
    if (isMuted) {
      this.isDeviceMuted = true
    } else {
      this.isDeviceMuted = false
    }
    if (this.g711Processor) {
      this.g711Processor.port.postMessage({ type: 'mute', value: isMuted })
    }
  }

  adjustVolume(volume) {
    this.g711Processor.port.postMessage({ type: 'gain', value: volume })
  }

  addEventListenerWebSocket(
    callbackCallActiveAfterBuffering,
    callbakDeviceBuffering,
    callbackCallsOnGoing,
    callbackCallsFailed,
    callbackWsError
  ) {
    return {
      onopen: () => {
        // eslint-disable-next-line no-console
        console.log('WebSocket voip connection opened')
      },
      onmessage: (event) => {
        const data = event.data
        if (data instanceof Blob) {
          if (!this.isMessageHaveBeenReceived) {
            this.isMessageHaveBeenReceived = true
            this.startMessageCheck(callbakDeviceBuffering)
          }
          if (this.isDeviceBuffering) {
            callbackCallActiveAfterBuffering()
            this.isDeviceBuffering = false
          }
          this.lastMessageTimestamp = Date.now()
          callbackCallsOnGoing()
          if (!this.isDeviceMuted) {
            playG711Audio(data, this.socket.workletNode)
          }
        } else {
          this.vueInstance.$mcToast.error(this.vueInstance.$t('global.somethingWentWrong'))
          this.stopCapture()
          this.stopMessageCheck()
        }
      },
      onclose: (event) => {
        // eslint-disable-next-line no-console
        console.log('WebSocket voip connection closed:', event.code, event.reason)
        callbackCallsFailed(event.reason)
        this.stopMessageCheck()
        this.isMessageHaveBeenReceived = false
      },
      onerror: (error) => {
        // eslint-disable-next-line no-console
        console.error('WebSocket voip error:', error)
        callbackWsError()
        this.stopMessageCheck()
        this.isMessageHaveBeenReceived = false
        this.vueInstance.$mcToast.error(this.vueInstance.$t('global.somethingWentWrong'))
      },
    }
  }

  startMessageCheck(callbakDeviceBuffering) {
    this.checkMessageInterval = setInterval(() => {
      const timeSinceLastMessage = Date.now() - this.lastMessageTimestamp

      if (timeSinceLastMessage > this.messageTimeout) {
        // No message received for more than 3 seconds
        this.isDeviceBuffering = true
        callbakDeviceBuffering()
      }
    }, 1000)
  }

  stopMessageCheck() {
    if (this.checkMessageInterval) {
      clearInterval(this.checkMessageInterval)
    }
  }
}
